import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import EmsLogo from '@/components/EmsLogo'
import { getCollegeByAdminEmail } from '@/utils/userAccess'

interface ConfiguredSubject {
  id: number
  student_group?: string
  subject_name?: string
  college_eiin: number | string
}

export default function AddStudentPage() {
  const navigate = useNavigate()
  const [collegeEiin, setCollegeEiin] = useState<number | string | null>(null)
  const [collegeName, setCollegeName] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  // DB Configured Subjects list
  const [dbSubjects, setDbSubjects] = useState<ConfiguredSubject[]>([])

  const [formData, setFormData] = useState({
    student_name_en: '',
    student_name_bn: '',
    class_roll: '',
    group: 'Science',
    session: '2025-2026',
    father_name_en: '',
    mother_name_en: '',
    mobile: '',
    gender: 'Male',
    ssc_roll: '',
    ssc_board: 'Comilla',
    ssc_year: '2024',
    reg_no: '',
    subject_1: '',
    subject_2: '',
    subject_3: '',
    subject_4: '',
    student_photo_url: '',
    blood_group: '',
    comment: ''
  })

  // Load college and session_subject list
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) {
        navigate('/login', { replace: true })
        return
      }
      const college = await getCollegeByAdminEmail(user.email)
      if (college) {
        setCollegeEiin(college.college_eiin)
        setCollegeName(college.college_name)

        // Fetch subjects from session_subject table for this college
        const { data: subData, error } = await supabase
          .from('session_subject')
          .select('*')
          .eq('college_eiin', college.college_eiin)

        if (!error && subData) {
          setDbSubjects(subData)
        }
      }
    })
  }, [navigate])

  // Filter subjects based on selected group
  const availableGroupSubjects = useMemo(() => {
    if (!formData.group) return dbSubjects.map(s => s.subject_name).filter(Boolean) as string[]
    const normGroup = formData.group.toLowerCase().trim()
    const filtered = dbSubjects.filter(s => {
      const g = (s.student_group || '').toLowerCase().trim()
      return g === normGroup || g === 'general' || !g
    })
    const names = filtered.map(s => s.subject_name?.trim()).filter(Boolean) as string[]
    return Array.from(new Set(names))
  }, [dbSubjects, formData.group])

  const getSelectableSubjects = (currentField: 'subject_1' | 'subject_2' | 'subject_3' | 'subject_4') => {
    const otherSelected = [
      currentField !== 'subject_1' ? formData.subject_1 : '',
      currentField !== 'subject_2' ? formData.subject_2 : '',
      currentField !== 'subject_3' ? formData.subject_3 : '',
      currentField !== 'subject_4' ? formData.subject_4 : ''
    ].filter(Boolean)

    return availableGroupSubjects.filter(sub =>
      sub === formData[currentField] || !otherSelected.includes(sub)
    )
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    if (name === 'group') {
      setFormData(prev => ({
        ...prev,
        group: value,
        subject_1: '',
        subject_2: '',
        subject_3: '',
        subject_4: ''
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!collegeEiin) {
      setNotice({ type: 'err', text: 'College EIIN missing. Please re-login.' })
      return
    }

    if (!formData.student_name_en.trim()) {
      setNotice({ type: 'err', text: 'Student name (English) is required' })
      return
    }

    setSaving(true)
    setNotice(null)

    try {
      const payload = {
        student_name_en: formData.student_name_en.trim(),
        student_name_bn: formData.student_name_bn.trim() || null,
        class_roll: formData.class_roll ? Number(formData.class_roll) : null,
        group: formData.group,
        session: formData.session.trim(),
        father_name_en: formData.father_name_en.trim() || null,
        mother_name_en: formData.mother_name_en.trim() || null,
        mobile: formData.mobile.trim() ? Number(formData.mobile) : null,
        gender: formData.gender,
        ssc_roll: formData.ssc_roll ? Number(formData.ssc_roll) : null,
        ssc_board: formData.ssc_board || null,
        ssc_year: formData.ssc_year ? Number(formData.ssc_year) : null,
        reg_no: formData.reg_no ? Number(formData.reg_no) : null,
        subject_1: formData.subject_1.trim() || null,
        subject_2: formData.subject_2.trim() || null,
        subject_3: formData.subject_3.trim() || null,
        subject_4: formData.subject_4.trim() || null,
        student_photo_url: formData.student_photo_url.trim() || null,
        blood_group: formData.blood_group.trim() || null,
        comment: formData.comment.trim() || null,
        college_eiin: collegeEiin // EIIN guarantee
      }

      const { data: resData, error } = await supabase
        .from('database_fgc')
        .insert([payload])
        .select('student_iid')

      if (error) throw error

      const newId = resData && resData[0] ? resData[0].student_iid : 'Success'
      setNotice({ type: 'ok', text: `Student added successfully! (Student IID: ${newId}, College EIIN: ${collegeEiin})` })

      // Reset form
      setFormData({
        student_name_en: '',
        student_name_bn: '',
        class_roll: '',
        group: 'Science',
        session: '2025-2026',
        father_name_en: '',
        mother_name_en: '',
        mobile: '',
        gender: 'Male',
        ssc_roll: '',
        ssc_board: 'Comilla',
        ssc_year: '2024',
        reg_no: '',
        subject_1: '',
        subject_2: '',
        subject_3: '',
        subject_4: '',
        student_photo_url: '',
        blood_group: '',
        comment: ''
      })
    } catch (err: any) {
      console.error('Add student error:', err)
      setNotice({ type: 'err', text: err?.message || 'Failed to add student' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <EmsLogo size={36} />
          <button onClick={() => navigate('/dashboard')} style={styles.btnBack}>
            ← Dashboard
          </button>
          <div>
            <h1 style={styles.title}>Add New Student</h1>
            <p style={styles.subtitle}>{collegeName} (EIIN: {collegeEiin})</p>
          </div>
        </div>

        <button onClick={() => navigate('/datalist')} style={styles.btnViewAll}>
          📚 View All Students
        </button>
      </header>

      {/* Form Container */}
      <main style={styles.main}>
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>➕ New Student Registration</h2>
          <p style={styles.cardSub}>Enter student details. Linked with College EIIN: {collegeEiin || '...'}</p>

          {notice && (
            <div style={notice.type === 'ok' ? styles.successBox : styles.errorBox}>
              {notice.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.sectionHeader}>Basic Information</div>
            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>Student Name (English) *</label>
                <input
                  type="text"
                  name="student_name_en"
                  placeholder="e.g. Tanvir Ahmed"
                  style={styles.input}
                  value={formData.student_name_en}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Student Name (Bangla)</label>
                <input
                  type="text"
                  name="student_name_bn"
                  placeholder="যেমন: তানভীর আহমেদ"
                  style={styles.input}
                  value={formData.student_name_bn}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div style={styles.grid3}>
              <div>
                <label style={styles.label}>Class Roll</label>
                <input
                  type="number"
                  name="class_roll"
                  placeholder="e.g. 101"
                  style={styles.input}
                  value={formData.class_roll}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label style={styles.label}>Group *</label>
                <select name="group" style={styles.input} value={formData.group} onChange={handleChange}>
                  <option value="Science">Science</option>
                  <option value="Humanities">Humanities</option>
                  <option value="Business Studies">Business Studies</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Session</label>
                <input
                  type="text"
                  name="session"
                  placeholder="e.g. 2025-2026"
                  style={styles.input}
                  value={formData.session}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div style={styles.sectionHeader}>Parents & Contact Info</div>
            <div style={styles.grid3}>
              <div>
                <label style={styles.label}>Father Name (English)</label>
                <input
                  type="text"
                  name="father_name_en"
                  placeholder="Father's full name"
                  style={styles.input}
                  value={formData.father_name_en}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label style={styles.label}>Mother Name (English)</label>
                <input
                  type="text"
                  name="mother_name_en"
                  placeholder="Mother's full name"
                  style={styles.input}
                  value={formData.mother_name_en}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label style={styles.label}>Mobile Number</label>
                <input
                  type="text"
                  name="mobile"
                  placeholder="017XXXXXXXX"
                  style={styles.input}
                  value={formData.mobile}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>Gender</label>
                <select name="gender" style={styles.input} value={formData.gender} onChange={handleChange}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Blood Group Dropdown</label>
                <select name="blood_group" style={styles.input} value={formData.blood_group} onChange={handleChange}>
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
            </div>

            <div style={styles.sectionHeader}>SSC Academic Info</div>
            <div style={styles.grid4}>
              <div>
                <label style={styles.label}>SSC Roll</label>
                <input
                  type="number"
                  name="ssc_roll"
                  placeholder="SSC Roll"
                  style={styles.input}
                  value={formData.ssc_roll}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label style={styles.label}>SSC Board Dropdown</label>
                <select name="ssc_board" style={styles.input} value={formData.ssc_board} onChange={handleChange}>
                  <option value="Comilla">Comilla</option>
                  <option value="Dhaka">Dhaka</option>
                  <option value="Chittagong">Chittagong</option>
                  <option value="Rajshahi">Rajshahi</option>
                  <option value="Jessore">Jessore</option>
                  <option value="Barisal">Barisal</option>
                  <option value="Sylhet">Sylhet</option>
                  <option value="Dinajpur">Dinajpur</option>
                  <option value="Mymensingh">Mymensingh</option>
                  <option value="Madrasah">Madrasah</option>
                  <option value="Technical">Technical</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>SSC Year</label>
                <input
                  type="number"
                  name="ssc_year"
                  placeholder="2024"
                  style={styles.input}
                  value={formData.ssc_year}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label style={styles.label}>Reg No</label>
                <input
                  type="number"
                  name="reg_no"
                  placeholder="Registration No"
                  style={styles.input}
                  value={formData.reg_no}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div style={styles.sectionHeader}>Subjects ({formData.group} Group configured in session_subject)</div>
            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>Subject 1</label>
                <select name="subject_1" style={styles.input} value={formData.subject_1} onChange={handleChange}>
                  <option value="">Select Subject 1</option>
                  {getSelectableSubjects('subject_1').map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Subject 2</label>
                <select name="subject_2" style={styles.input} value={formData.subject_2} onChange={handleChange}>
                  <option value="">Select Subject 2</option>
                  {getSelectableSubjects('subject_2').map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Subject 3</label>
                <select name="subject_3" style={styles.input} value={formData.subject_3} onChange={handleChange}>
                  <option value="">Select Subject 3</option>
                  {getSelectableSubjects('subject_3').map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Subject 4 (Optional)</label>
                <select name="subject_4" style={styles.input} value={formData.subject_4} onChange={handleChange}>
                  <option value="">Select Subject 4</option>
                  {getSelectableSubjects('subject_4').map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: '14px' }}>
              <label style={styles.label}>Student Photo URL</label>
              <input
                type="text"
                name="student_photo_url"
                placeholder="https://..."
                style={styles.input}
                value={formData.student_photo_url}
                onChange={handleChange}
              />
            </div>

            <div style={{ marginTop: '24px', textAlign: 'right' }}>
              <button type="submit" style={styles.btnSubmit} disabled={saving}>
                {saving ? 'Adding Student...' : '➕ Save & Register Student'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
    background: '#f8fafc',
    minHeight: '100vh',
    color: '#0f172a'
  },
  header: {
    background: '#ffffff',
    padding: '16px 32px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 50
  },
  btnBack: {
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    color: '#475569',
    padding: '6px 14px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '13px'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 900,
    color: '#0f172a'
  },
  subtitle: {
    margin: 0,
    fontSize: '11px',
    color: '#ff5500',
    fontWeight: 800
  },
  btnViewAll: {
    background: '#f1f5f9',
    color: '#0f172a',
    border: '1px solid #cbd5e1',
    padding: '8px 16px',
    borderRadius: '10px',
    fontWeight: 800,
    fontSize: '13px',
    cursor: 'pointer'
  },
  main: {
    maxWidth: '850px',
    margin: '32px auto',
    padding: '0 20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '24px',
    padding: '32px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.05)'
  },
  cardTitle: {
    margin: 0,
    fontSize: '1.4rem',
    fontWeight: 900,
    color: '#0f172a'
  },
  cardSub: {
    margin: '4px 0 24px',
    fontSize: '13px',
    color: '#64748b'
  },
  sectionHeader: {
    fontSize: '12px',
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#ff5500',
    borderBottom: '1px solid #fee2e2',
    paddingBottom: '6px',
    marginTop: '20px',
    marginBottom: '14px'
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px'
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '14px',
    marginTop: '12px'
  },
  grid4: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: '14px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#475569'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    background: '#fff'
  },
  btnSubmit: {
    padding: '12px 28px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #ff7a00, #ff5500)',
    color: '#ffffff',
    fontWeight: 900,
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(255, 85, 0, 0.3)'
  },
  successBox: {
    padding: '14px 18px',
    background: '#dcfce7',
    color: '#15803d',
    borderRadius: '12px',
    marginBottom: '20px',
    fontSize: '15px',
    fontWeight: 700
  },
  errorBox: {
    padding: '14px 18px',
    background: '#fee2e2',
    color: '#b91c1c',
    borderRadius: '12px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: 700
  }
}
